/**
 * Assign Study Dialog — pick a user to assign the selected studies to.
 *
 * Uses getUsersPaged to list available users, then calls assignStudy
 * with the selected user's objKey.
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItemButton, ListItemText,
  Box, CircularProgress, Typography, InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import type { Study, User } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface AssignStudyDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete?: () => void;
}

export const AssignStudyDialog = ({ open, studies, onClose, onComplete }: AssignStudyDialogProps) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Load users when dialog opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setFilter('');
    setSelectedUser(null);
    api.getUsersPaged(0, 500)
      .then((result) => setUsers(result.records))
      .catch((e) => pushError('Failed to load users: ' + (e instanceof Error ? e.message : String(e))))
      .finally(() => setLoading(false));
  }, [open]);

  const filteredUsers = filter.trim()
    ? users.filter((u) =>
        u.name.toLowerCase().includes(filter.toLowerCase()) ||
        u.user.toLowerCase().includes(filter.toLowerCase()),
      )
    : users;

  const handleAssign = async () => {
    if (!selectedUser || studies.length === 0) return;
    setAssigning(true);
    try {
      const objKeys = studies.map((s) => s.objKey);
      await api.assignStudy(objKeys, selectedUser.objKey);
      pushSuccess(`Assigned ${studies.length} study/studies to ${selectedUser.name}`);
      onComplete?.();
      onClose();
    } catch (e) {
      pushError('Assign failed: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
          maxHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
        Assign {studies.length > 1 ? `${studies.length} Studies` : 'Study'} to User
      </DialogTitle>
      <DialogContent>
        {studies.length === 1 && (
          <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
            {studies[0].name} — {studies[0].acc} ({studies[0].mod})
          </Typography>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Filter users…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: 13 } }}
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 280,
              overflow: 'auto',
              border: `1px solid ${gwtStyles.borderSubtle}`,
              borderRadius: '4px',
            }}
          >
            {filteredUsers.map((user) => (
              <ListItemButton
                key={user.objKey}
                selected={selectedUser?.objKey === user.objKey}
                onClick={() => setSelectedUser(user)}
                sx={{ py: 0.5 }}
              >
                <ListItemText
                  primary={user.name}
                  secondary={`${user.user}${user.subType ? ` · ${user.subType}` : ''}`}
                  primaryTypographyProps={{ fontSize: 13 }}
                  secondaryTypographyProps={{ fontSize: 11 }}
                />
              </ListItemButton>
            ))}
            {filteredUsers.length === 0 && (
              <Typography sx={{ py: 2, textAlign: 'center', fontSize: 12, color: 'text.secondary' }}>
                No users found
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!selectedUser || assigning}
          onClick={handleAssign}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {assigning ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
